/**
  * @class meleWindow
  * @description
  *
  * # Purpose
  * Implements `<mele-window>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-window></mele-window>';
  * ```
  * Create element in JavaScript:
  * ```
  * var el = elParent.appendChildren({
  *   tag: 'mele-window',
  *   mod: 'mmodUnits',
  *   layout: 'split',
  *   views: 'list,detail',
  *   active: 'list',
  * })
  * el.updateProperties({
  *   layout: 'triplet',
  *   views: 'list,detail,subdetail'
  * })
  * el.active = 'detail';
  * ```
  *
  * # Implementation notes
  *
  */

import meleContainer from 'meleContainer'

const PROPERTIES = _.extend({}, {
  mod: { type: 'string', description: "unique ID of window's module", readonly: true },
  x: { type: 'number', description: "position of window's left side in pixels", readonly: true },
  y: { type: 'number', description: "position of window's top side in pixels", readonly: true },
  offset: { type: 'number', description: 'global offset for all windows', default: 0 },
  width: { type: 'number', description: "window's width", readonly: true },
  height: { type: 'number', description: "window's height", readonly: true },
  z: { type: 'number', description: "window's z-index", readonly: true },
  focused: { type: 'boolean', description: 'true if window is focused', readonly: true },
  behavior: { type: 'string', description: "window's behavior", readonly: true },
  orientation: { type: 'string', description: "window's orientation", readonly: true },
  splitheight: { type: 'string', description: 'height of views within window when sidesplit layout active' },
  minsplitheight: { type: 'number', description: 'minimum height of sidesplit bottom from top', default: 200 },
  // language: { type: "string", description: "window's language", readonly: true },
  layout: { type: 'string', description: 'layout of visible views (single,split,triplet,sidesplit)' },
  views: { type: 'string', description: 'list of currently visible views separated by comma' },
  active: { type: 'number', description: 'which of views is active; 0 means first', default: 0 },
  eventViewChanged: { type: 'function', description: 'method that can be overwritten to get information on changed active view' },
}, meleContainer._properties)

export default class meleWindow extends meleContainer {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * signature of element - string identification
  */
  signature() {
    const id = this.mod
    if(id!==undefined && id!==null && String(id).length>0) return this.tagName+'['+id+']'
  } // signature

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    // insert system back button
    this._viewback = this.appendChildren({
      tag: 'mele-button',
      key: 'viewback',
      type: 'toolbar',
      iconleft: 'arrow.w',
      hidden: true,
      eventButtonClicked: ()=>{ this.eventViewBack(this.active) },
    })
    this._printheader = this.appendChildren({
      tag: 'mele-text',
      key: 'print_header',
    })
    this._printfooter = this.appendChildren({
      tag: 'mele-text',
      key: 'print_footer',
    })
    this._body = this.appendChildren({ tag: 'mele-body' })
    this.active = parseInt(this.active) || 0
  } // init

  xChanged(value) {
    if(!this.style) return
    value = parseInt(value) || 0
    const offset = parseInt(this.offset) || 0
    if(value>=0) {
      this.style.left = (value+offset)+'px'
      this.style.right = ''
    }else {
      this.style.right = (-value+offset)+'px'
      this.style.left = ''
    }
  } // xChanged

  yChanged(value) {
    if(!this.style) return
    value = parseInt(value) || 0
    if(value>=0) {
      this.style.top = (value)+'px'
      this.style.bottom = ''
    }else {
      this.style.bottom = (-value)+'px'
      this.style.top = ''
    }
  } // yChanged

  widthChanged(value) {
    if(!this.style) return
    value = parseInt(value)
    if(value<0) value = 0
    const offset = parseInt(this.offset) || 0
    this.style.width = (value-offset*2)+'px'
  } // widthChanged

  heightChanged(value) {
    if(!this.style) return
    value = parseInt(value)
    if(value<0) value = 0
    this.style.height = (value)+'px'
  } // heightChanged

  zChanged(value) {
    if(!this.style) return
    value = parseInt(value)
    if(!(value>=0)) value = ''
    this.style.zIndex = value
  } // zChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * change of layout/view/active will invoke recalculation of children's role attribute
  */
  layoutChanged() { this.render() }
  viewsChanged() {
    this.determineActiveView()
    this.render()
  }

  activeChanged(value, old) {
    this.render()
    this.eventViewChanged(value, old)
  }

  behaviorChanged(value) {
    this.render()

    // forward behavior change to all modal windows
    if(!this.classList.contains('mele-modal') && !this.classList.contains('mele-modalwindow')) {
      this.closest('body').forAll('>.mele-modal,>.mele-modalwindow', el=>el.behavior=value)
    }

    // forward behavior change to all views
    this.forAll('.mele-view', el=>el.behavior=value)
  }

  splitheightChanged(value) {
    if(this.elBottom) {
      this.elBottom.style.height = /^\d+%$/.test(value) ? value : isNaN(parseInt(value)) ? '' : (parseInt(value)+'px')
    }
  } // splitheightChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculate role attribute of children with view attribute
  */
  render() {
    this.elBottom = null
    let views = _.toString(this.views)
    views = views=='' ? [] : views.split(',')
    let active = parseInt(this.active)
    if(!(active>0)) active = 0

    const layoutid = this.behavior=='phone' ? 'single' : this.layout
    const layout_roles = {
      single: ['single'],
      split: ['left', 'right'],
      triplet: ['left', 'center', 'right'],
      sidesplit: ['left', 'top', 'bottom'],
    }

    const roles = layout_roles[layoutid] || []
    let first = active-roles.length+1
    if(first<0) first=0
    const last = first+roles.length-1
    const view_roles = {}
    let viewback = views.length>0
    for(let i=0; i<views.length; i++) {
      let role = i>=first && i<=last ? roles[i-first] : 'none'
      if(role=='left' || this.behavior=='phone') { role += ' narrow' }
      if(i==first && i==0) { role+=' first'; viewback=false } else if(i==first) role+=' back'
      else if(i==last && i==views.length) role+=' next last'
      else if(i==last) role+=' next'
      view_roles[views[i]] = role
    }
    this._viewback.hidden = !viewback

    this.forAll('[view]', (child_view)=>{
      const child_key = child_view.key || child_view.getAttribute('key') || ''
      if(typeof child_view.setProperty==='function') {
        child_view.setProperty('role', view_roles[child_key] || 'none', false)
      }else {
        child_view.setAttribute('role', view_roles[child_key] || 'none')
      }
    })

    // tunr off any previous sidesplit resizers
    this.forAll('[view] >mele-wrapper >mele-header-wrapper', el=>{
      el.onmousedown = null
    })
    if(layoutid=='sidesplit') {
      // turn on side split resizer
      this.elBottom = this.find('[view][role*=bottom]')
      const elBottomHeader = this.elBottom ? this.elBottom.find('>mele-wrapper >mele-header-wrapper') : null
      if(elBottomHeader) elBottomHeader.onmousedown = this.startResizing.bind(this, this.elBottom)

      this.splitheightChanged(this.splitheight)
    }

    // fixed with method determineActiveView() which is called from focusedChanged and ViewsChanged
    // var active_view = _.toString(views[active]);
    // this.forAll('[view]', el=>el.focused = el.key==active_view);
  }

  focusedChanged() {
    this.determineActiveView()
    const event_name = this.focused ? 'eventWindowFocused' : 'eventWindowUnfocused'
    this.forAll('*', el=>{
      try{ el[event_name]() } catch(e) {}
    })
  }

  determineActiveView() {
    let active_view = 0
    if(this.focused) {
      this.parentElement.forAll('mele-window', el=>el.focused=el===this)
      let views = _.toString(this.views)
      views = views=='' ? [] : views.split(',')
      let active = parseInt(this.active)
      if(isNaN(active) || active<0) {
        this.active = active = 0
      }else if(active>=views.length) {
        this.active = active = views.length-1
      }
      active_view = _.toString(views[active])
    }
    this.forAll('[view]', el=>el.focused = el.key==active_view)
  }

  startResizing(elBottom, event) {
    const { target } = event
    if(target.closest('mele-button')) return
    this.classList.add('rowresize')
    this.onmousemove = this.moveResizing.bind(this, event.clientY, parseInt(elBottom.offsetHeight))
    this.onmouseup = this.stopResizing.bind(this)
  } // startResizing

  moveResizing(startY, bottomHeight, event) {
    const offsetY = event.clientY - startY
    const newHeight = bottomHeight - offsetY
    const maxHeight = this.offsetHeight - this.minsplitheight
    this.splitheight = newHeight < maxHeight ? newHeight : maxHeight
  } // moveResizing

  stopResizing() {
    this.onmousemove = null
    this.onmouseup = null
    this.onmouseout = null
    this.classList.remove('rowresize')
  } // stopResizing

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * called after going to previous view
  */
  eventViewChanged(active_view) {
  } // eventViewChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * called after going to previous view
  */
  eventViewBack(active_view) {
    let active = parseInt(active_view)
    if(isNaN(active)) active = parseInt(this.active)
    if(active>0) this.active = active-1
  } // eventViewBack
} // class meleWindow

meleWindow._properties = PROPERTIES
meleWindow.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-window', meleWindow)
